// #ux-redesign-start: PasskeysForm (Account > Security · WebAuthn)
//
// Gestión de las passkeys del usuario logueado. Consume los endpoints que ya
// existían en MS-Auth y que hasta ahora no tenían UI:
//   GET    /auth/patients/passkey                  → listar
//   DELETE /auth/patients/passkey/:credentialId    → desvincular
//
// Desvincular acá borra la credential del lado del servidor. La copia que el
// navegador guarda en su llavero NO se puede borrar por API (WebAuthn no lo
// permite): queda huérfana hasta que el usuario la saque desde la configuración
// del sistema operativo. Por eso el texto lo aclara — si no, la passkey sigue
// apareciendo en el selector del browser y da la sensación de que no se borró.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslate } from 'react-translate';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  LoadingBlock,
  toast,
} from 'components/ds';
import {
  passkeyService,
  readPasskeyHint,
  clearPasskeyHint,
} from 'services/passkeyService';

const KeyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

// Fechas: el backend manda ISO (createdAt) y puede mandar null (lastUsedAt si
// la passkey se enroló pero nunca se usó para entrar).
const formatDate = (value, locale) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(locale || undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const PasskeysForm = () => {
  const t = useTranslate('passkeys');

  const [loading, setLoading] = useState(true);
  const [passkeys, setPasskeys] = useState([]);
  const [removing, setRemoving] = useState(null); // credentialId en curso
  const [pendingRemoval, setPendingRemoval] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await passkeyService.list();
    if (result.ok) {
      setPasskeys(Array.isArray(result.data) ? result.data : []);
    } else {
      // 404 = el user no tiene passkeys, no es un error. 401 tampoco se avisa:
      // el interceptor de useSessionTimer ya está cerrando la sesión y el toast
      // sólo alcanzaría a parpadear sobre la pantalla de login.
      if (result.status !== 404 && result.status !== 401) toast.error(t('load-error'));
      setPasskeys([]);
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const onConfirmRemove = async () => {
    const target = pendingRemoval;
    setPendingRemoval(null);
    if (!target) return;

    setRemoving(target.credentialId);
    const result = await passkeyService.remove(target.credentialId);
    setRemoving(null);

    if (!result.ok && result.status !== 404) {
      toast.error(t('remove-error'));
      return;
    }

    // 404 = ya no estaba del lado del servidor: el objetivo igual se cumplió.
    const rest = passkeys.filter((p) => p.credentialId !== target.credentialId);
    setPasskeys(rest);

    // Si no queda ninguna, el hint local apunta a una cuenta sin passkeys y
    // dejaría el botón de login pidiendo una llave que ya no existe.
    if (!rest.length && readPasskeyHint()) clearPasskeyHint();

    toast.success(t('remove-success'));
  };

  const busy = Boolean(removing);

  return (
    <div className="setting-section">
      <ConfirmDialog
        isOpen={Boolean(pendingRemoval)}
        title={t('remove-confirm-title')}
        description={t('remove-confirm-description')}
        variant="danger"
        confirmLabel={t('btn-remove')}
        cancelLabel={t('btn-cancel')}
        onConfirm={onConfirmRemove}
        onCancel={() => setPendingRemoval(null)}
      />

      <h5 className="general-card-subtitle">{t('title')}</h5>
      <p className="ds-passkey-section__intro">{t('description')}</p>

      {loading && <LoadingBlock size="sm" padded="md" ariaLabel={t('loading')} />}

      {!loading && !passkeys.length && (
        <EmptyState
          variant="compact"
          icon={<KeyIcon />}
          title={t('empty-title')}
          description={t('empty-description')}
        />
      )}

      {!loading && passkeys.length > 0 && (
        <ul className="ds-passkey-list">
          {passkeys.map((passkey) => {
            const created = formatDate(passkey.createdAt);
            const lastUsed = formatDate(passkey.lastUsedAt);
            return (
              <li key={passkey.credentialId} className="ds-passkey-list__item">
                <div className="ds-passkey-list__info">
                  <span className="ds-passkey-list__name">
                    <KeyIcon />
                    {passkey.deviceName || t('unknown-device')}
                  </span>
                  <span className="ds-passkey-list__meta">
                    {created && `${t('created-at')}: ${created}`}
                    {created && lastUsed && ' · '}
                    {lastUsed
                      ? `${t('last-used-at')}: ${lastUsed}`
                      : (!created ? null : ` · ${t('never-used')}`)}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  loading={removing === passkey.credentialId}
                  disabled={busy}
                  onClick={() => setPendingRemoval(passkey)}
                >
                  {t('btn-remove')}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PasskeysForm;
// #ux-redesign-end
